import { useEffect, useRef, useState } from 'react';
import { BuildingIcon, ChevronDownIcon, CloseIcon, PersonIcon } from './mailRuleIcons';
import MailRuleClearInput from './MailRuleClearInput';

/**
 * 網域：每段 1～63 字、只能英數與連字號（不可開頭或結尾）、最後一段是 2 字以上的英文。
 * ⛔ 與 mygenieap MailRuleValidator 的 DOMAIN 同一條規則，改這裡就要一起改後端（對話工具直接打後端）。
 */
const DOMAIN_PATTERN = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
/** 帳號（@ 前）只收英數與信箱允許的半形符號；AGUI-1298：原本只擋空白與 @，「哈哈」這類中文帳號也會被加入 */
const LOCAL_PART_PATTERN = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
const isEmail = (v) => {
  const at = v.lastIndexOf('@');
  return at > 0 && LOCAL_PART_PATTERN.test(v.slice(0, at)) && DOMAIN_PATTERN.test(v.slice(at + 1));
};

export const COMPANY_DOMAIN = 'taiwanmobile.com';

/**
 * 輸入列右側的下拉：公司同事只打帳號；外部寄件者打完整信箱；或是整個網域。
 * AGUI-1264：提示文案縮短，電腦版輸入框寬度內要放得下、不被截掉。
 * AGUI-1298：範例改全小寫——加入時一律轉小寫，範例寫成大小寫混合會讓人以為大小寫有差。
 */
export const SENDER_MODES = {
  company: { label: `@${COMPANY_DOMAIN}`, placeholder: '輸入帳號，例如 mybro' },
  email: { label: '其他信箱', placeholder: '例如 name@abc.com' },
  domain: { label: '整個網域', placeholder: '例如 abc.com' },
};

/** 後端 MailRuleValidator 的 MAX_ITEMS（信箱、網域各自計算） */
const MAX_ITEMS = 20;

/**
 * APISIX `sender_addresses` 每個 ≤ 254 字、`sender_domains` 每個 ≤ 253 字（OpenAPI RuleConditions）。
 * AGUI-1285：檢查的是組出來的完整值，不是輸入框裡打的字——公司帳號會自動接上「@taiwanmobile.com」（17 字），
 * 輸入框的 maxLength 管不到；而用 maxLength 擋「其他信箱」又會把超過的部分悄悄截掉、變成另一個錯的信箱。
 */
export const MAX_ADDRESS_LENGTH = 254;
export const MAX_DOMAIN_LENGTH = 253;
/**
 * 帳號（@ 前）≤ 64 字（RFC 5321）。先於完整信箱檢查：公司帳號超過 64 字時，
 * 「帳號太長」比「完整信箱太長」貼近使用者打的內容；也免得通過 254 字檢查，按「儲存」才被 APISIX 以格式不符擋下
 */
export const MAX_LOCAL_PART_LENGTH = 64;

const checkLength = (parsed) => {
  if (parsed.kind === 'address') {
    const local = parsed.value.slice(0, parsed.value.lastIndexOf('@'));
    if (local.length > MAX_LOCAL_PART_LENGTH) {
      return { error: `帳號（@ 前面）不可超過 ${MAX_LOCAL_PART_LENGTH} 字（目前 ${local.length} 字）` };
    }
  }
  const max = parsed.kind === 'address' ? MAX_ADDRESS_LENGTH : MAX_DOMAIN_LENGTH;
  if (parsed.value.length <= max) return parsed;
  return {
    error: parsed.kind === 'address'
      ? `完整信箱不可超過 ${max} 字（目前 ${parsed.value.length} 字）`
      : `網域不可超過 ${max} 字（目前 ${parsed.value.length} 字）`,
  };
};

/**
 * 依輸入內容判斷是信箱還是網域，不完全照下拉：貼上完整信箱或打了「@abc.com」時不必先切換。
 * @returns {{ kind: 'address'|'domain', value: string } | { error: string }}
 */
export function parseSender(raw, mode) {
  const parsed = parseSenderFormat(raw, mode);
  return parsed.error === undefined ? checkLength(parsed) : parsed;
}

function parseSenderFormat(raw, mode) {
  // D-1022-06：逗號是一般字元，不再去掉結尾的逗號（「a@b.com,」加入時判為格式錯誤）
  const v = raw.trim();
  if (!v) return { error: null };
  const stripped = v.replace(/^@+/, '').toLowerCase();
  if (mode === 'domain') {
    if (stripped.includes('@') && isEmail(stripped)) return { kind: 'address', value: stripped };
    return DOMAIN_PATTERN.test(stripped)
      ? { kind: 'domain', value: stripped }
      : { error: `「${v}」不是有效的網域，例如 abc.com` };
  }
  // 「@abc.com」是使用者照信箱的樣子寫網域，當成網域；
  // ⛔ 後面不是合法網域時（例如「@@#」）照帳號／信箱的格式說明，不說成網域錯誤（AGUI-1271）
  if (v.startsWith('@') && DOMAIN_PATTERN.test(stripped)) return { kind: 'domain', value: stripped };
  const address = (mode === 'company' && !v.includes('@') ? `${v}@${COMPANY_DOMAIN}` : v).toLowerCase();
  if (isEmail(address)) return { kind: 'address', value: address };
  return mode === 'company'
    ? { error: `「${v}」不是有效的帳號，例如 mybro` }
    : { error: `「${v}」不是有效的信箱，請輸入完整信箱，例如 name@abc.com` };
}

/**
 * 寄件者清單（AGUI-1113 10-05）：信箱與網域合成一張清單，每列一位，符合任一位即可。
 *
 * 取代原本「寄件者信箱」「寄件網域」兩個標籤輸入框：預帶在框裡的「@taiwanmobile.com」游標一偏就打錯，
 * 要按 Enter 才加入也不容易發現，兩格擇一又得靠說明文字講清楚。
 * 送出時仍拆回 `fromAddresses`／`fromDomains`，後端與規則複述都不必改。
 *
 * 離開輸入框時會補加還沒按「加入」的內容（比照標籤輸入），但焦點移到同一列的下拉或「加入」按鈕時不補加——
 * 否則使用者打完才想切到「其他信箱」，會先以錯的類型被加進去。
 *
 * @param {string[]} addresses 已加入的信箱
 * @param {string[]} domains 已加入的網域
 * @param {(next: {fromAddresses: string[], fromDomains: string[]}) => void} onChange
 * @param {'company'|'email'|'domain'} [initialMode] 範本帶入，例如「特定公司來信」一打開就是「整個網域」
 * @param {boolean} [invalid] 表單層級的錯誤（一位寄件者都沒有就按儲存）
 * @param {string} [describedBy] 表單層級錯誤訊息的 id
 * @param {string} [invalidMessage] 表單層級錯誤的文字；輸入框本身有格式錯誤時不顯示（AGUI-1264）
 * 輸入框不設 maxLength（AGUI-1285）：長度上限由加入時的 {@link parseSender} 檢查完整信箱／網域，超過就擋下並說明。
 *
 * @param {{ current: Function|null }} [pendingRef] 表單按「儲存」時呼叫，檢查輸入框裡有沒有還沒加入的字（10-27 使用者裁決）；
 *   有就在框下方說明並回 true（不該送出），框是空的回 false。不收進清單、不檢查格式
 *
 * 只有按「加入」或 Enter 才加入（D-1022-06）：離開輸入框、打逗號都不加入。
 */
export default function MailRuleSenderList({
  id, addresses, domains, onChange, initialMode = 'company', invalid = false, describedBy, invalidMessage, pendingRef,
}) {
  const [mode, setMode] = useState(SENDER_MODES[initialMode] ? initialMode : 'company');
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const inputRef = useRef(null);

  // 新加入的那列淡入一次（樣式見 .mail-rule-senders__item--new）；1.2 秒後拿掉 class，下次加入時動畫才會再跑
  useEffect(() => {
    if (!justAdded) return undefined;
    const timer = setTimeout(() => setJustAdded(null), 1200);
    return () => clearTimeout(timer);
  }, [justAdded]);

  /**
   * @param {boolean} [fromButton] 按「加入」鈕。AGUI-1267：公司帳號模式沒打帳號就按「加入」，
   *   等同「整個網域」填 taiwanmobile.com，直接加入公司網域。只限按鈕——Enter 與離開輸入框時沒打字照舊不加入，免得誤觸就收全公司的信
   */
  /**
   * 算出加入輸入框內容後的清單，不動 state。
   * @returns {{ next?: {fromAddresses: string[], fromDomains: string[]}, added?: string, error?: string|null }}
   *   沒有 next 就是沒加入；error 是要顯示的錯誤（沒打字時為 null）
   */
  const resolve = (fromButton) => {
    const parsed = fromButton && mode === 'company' && !text.trim()
      ? { kind: 'domain', value: COMPANY_DOMAIN }
      : parseSender(text, mode);
    if (parsed.error !== undefined) return { error: parsed.error };
    const list = parsed.kind === 'address' ? addresses : domains;
    if (list.includes(parsed.value)) return { error: `「${parsed.value}」已在清單中` };
    if (list.length >= MAX_ITEMS) return { error: `${parsed.kind === 'address' ? '信箱' : '網域'}最多 ${MAX_ITEMS} 個` };
    return {
      next: {
        fromAddresses: parsed.kind === 'address' ? [...addresses, parsed.value] : addresses,
        fromDomains: parsed.kind === 'domain' ? [...domains, parsed.value] : domains,
      },
      added: `${parsed.kind}:${parsed.value}`,
    };
  };

  const add = (fromButton = false) => {
    const result = resolve(fromButton);
    if (!result.next) {
      if (result.error) setError(result.error);
      return result;
    }
    onChange(result.next);
    setJustAdded(result.added);
    setText('');
    setError(null);
    return result;
  };

  // 10-27 使用者裁決（取代 AGUI-1283 的儲存時收進）：框裡還有字就擋下送出。已在清單中的用重複的文案，其他一律請使用者加入或清除
  useEffect(() => {
    if (!pendingRef) return undefined;
    pendingRef.current = () => {
      if (!text.trim()) return false;
      const parsed = parseSender(text, mode);
      const list = parsed.kind === 'address' ? addresses : domains;
      setError(parsed.error === undefined && list.includes(parsed.value)
        ? `「${parsed.value}」已在清單中`
        : '還有寄件者未加入，請按【加入】或清除後再儲存');
      return true;
    };
    return () => {
      pendingRef.current = null;
    };
  });

  const remove = (kind, value) =>
    onChange({
      fromAddresses: kind === 'address' ? addresses.filter((x) => x !== value) : addresses,
      fromDomains: kind === 'domain' ? domains.filter((x) => x !== value) : domains,
    });

  const items = [
    ...addresses.map((value) => ({ kind: 'address', value })),
    ...domains.map((value) => ({ kind: 'domain', value })),
  ];
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className={`mail-rule-senders${invalid ? ' mail-rule-senders--invalid' : ''}`}>
      {/* 輸入列固定在上，清單往下長：加越多輸入列也不會被推走 */}
      <div className="mail-rule-senders__add">
        <MailRuleClearInput label="寄件者輸入" value={text} inputRef={inputRef}
          onClear={() => {
            setText('');
            setError(null);
          }}>
          <input
            id={id}
            ref={inputRef}
            className={error || invalid ? 'mail-rule-form__input--invalid' : undefined}
            aria-label="新增寄件者"
            aria-invalid={Boolean(error) || invalid}
            aria-describedby={(error ? errorId : invalid && invalidMessage ? describedBy : undefined) || undefined}
            placeholder={SENDER_MODES[mode].placeholder}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
        </MailRuleClearInput>
        {/* 下拉與「加入」綁成一組：輸入列放不下時整組換到下一行，不會只剩「加入」掉下去（樣式見 .mail-rule-senders__controls） */}
        <span className="mail-rule-senders__controls">
          {/* 箭頭比照過濾規則的下拉（10-19）：自己畫、往左內縮、同色；這個下拉沒有「不限」，所以沒有清除鈕 */}
          <span className="mail-rule-form__select mail-rule-senders__mode">
            <select
              aria-label="寄件者類型"
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setError(null);
                inputRef.current?.focus();
              }}
            >
              {Object.entries(SENDER_MODES).map(([key, m]) => (
                <option key={key} value={key}>{m.label}</option>
              ))}
            </select>
            <ChevronDownIcon size={16} className="mail-rule-form__select-arrow" />
          </span>
          <button type="button" className="mail-rule-senders__add-button" onClick={() => add(true)}>
            加入
          </button>
        </span>
      </div>
      {/* AGUI-1264：輸入內容格式錯誤時只說格式錯在哪，不再疊「請至少加入一位寄件者」 */}
      {error ? (
        <div id={errorId} className="mail-rule-form__field-error" role="alert">{error}</div>
      ) : (
        // 10-22 使用者裁決：沒加寄件者時紅框下方恢復顯示紅字（推翻 10-21「只留紅框」，b667c24）
        invalid && invalidMessage && (
          <div id={describedBy} className="mail-rule-form__field-error" role="alert">{invalidMessage}</div>
        )
      )}

      {/* 10-19 使用者裁決：沒有寄件者時不放「尚未加入」虛線框，必填由輸入框紅框與錯誤訊息提示 */}
      {items.length > 0 && (
        <ul className="mail-rule-senders__list" aria-label="已加入的寄件者">
          {items.map(({ kind, value }) => (
            <li
              key={`${kind}:${value}`}
              className={`mail-rule-senders__item${justAdded === `${kind}:${value}` ? ' mail-rule-senders__item--new' : ''}`}
            >
              <span className="mail-rule-senders__icon" aria-hidden="true">{kind === 'address' ? <PersonIcon /> : <BuildingIcon />}</span>
              <span className="mail-rule-senders__value">{value}</span>
              {kind === 'domain' && <span className="mail-rule-senders__note">所有來自此網域的信</span>}
              <button type="button" className="mail-rule-senders__remove" aria-label={`移除 ${value}`}
                onClick={() => remove(kind, value)}>
                <CloseIcon size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
